import {AuthRepository} from '../../domain/auth/repositories/AuthRepository';
import {DashboardRepository} from '../../domain/dashboard/repositories/DashboardRepository';
import {ScanRepository} from '../../domain/scan/repositories/ScanRepository';
import {FormDataRepository} from '../../domain/shared/repositories/FormDataRepository';
import {LoginUseCase} from '../../domain/auth/usecases/LoginUseCase';
import {LogoutUseCase} from '../../domain/auth/usecases/LogoutUseCase';
import {GetCurrentUserUseCase} from '../../domain/auth/usecases/GetCurrentUserUseCase';
import {Send2FACodeUseCase} from '../../domain/auth/usecases/Send2FACodeUseCase';
import {Verify2FACodeUseCase} from '../../domain/auth/usecases/Verify2FACodeUseCase';
import {GetDashboardDataUseCase} from '../../domain/dashboard/usecases/GetDashboardDataUseCase';
import {GetDeliveriesByCreatedUserUseCase} from '../../domain/dashboard/usecases/GetDeliveriesByCreatedUserUseCase';
import {ScanQRCodeUseCase} from '../../domain/scan/usecases/ScanQRCodeUseCase';
import {GetAvailableServicesFromEligibilityUseCase} from '../../domain/scan/usecases/GetAvailableServicesFromEligibilityUseCase';
import {GetAssignmentByIdUseCase} from '../../domain/scan/usecases/GetAssignmentByIdUseCase';
import {CreateDeliveryUseCase} from '../../domain/scan/usecases/CreateDeliveryUseCase';
import {CreateAssignmentUseCase} from '../../domain/scan/usecases/CreateAssignmentUseCase';
import {UpdateDeliveryUseCase} from '../../domain/scan/usecases/UpdateDeliveryUseCase';
import {GetCaseDetailUseCase} from '../../domain/scan/usecases/GetCaseDetailUseCase';
import {GetAvailableServicesUseCase} from '../../domain/scan/usecases/GetAvailableServicesUseCase';
import {AuthService} from '../../application/services/AuthService';
import {DashboardService} from '../../application/services/DashboardService';
import {ScanService} from '../../application/services/ScanService';
import {FormDataService} from '../../application/services/FormDataService';
import {AuthRepositoryImpl} from '../../infrastructure/auth/AuthRepositoryImpl';
import {DashboardRepositoryImpl} from '../../infrastructure/dashboard/DashboardRepositoryImpl';
import {ScanRepositoryImpl} from '../../infrastructure/scan/ScanRepositoryImpl';
import {FormDataRepositoryImpl} from '../../infrastructure/shared/FormDataRepositoryImpl';
import {AsyncStorageAdapter} from '../../infrastructure/storage/AsyncStorageAdapter';
import {setGlobalRefreshCallback, setGlobalLogoutCallback} from '../http/apiClient';
import {API_CONFIG} from '../config/api.config';

export class ServiceContainer {
  private static instance: ServiceContainer;

  // Infrastructure
  private readonly storageAdapter: AsyncStorageAdapter;
  private readonly authRepository: AuthRepository;
  private readonly dashboardRepository: DashboardRepository;
  private readonly scanRepository: ScanRepository;
  private readonly formDataRepository: FormDataRepository;

  // Use Cases
  private readonly loginUseCase: LoginUseCase;
  private readonly logoutUseCase: LogoutUseCase;
  private readonly getCurrentUserUseCase: GetCurrentUserUseCase;
  private readonly send2FACodeUseCase: Send2FACodeUseCase;
  private readonly verify2FACodeUseCase: Verify2FACodeUseCase;
  private readonly getDashboardDataUseCase: GetDashboardDataUseCase;
  private readonly getDeliveriesByCreatedUserUseCase: GetDeliveriesByCreatedUserUseCase;
  private readonly scanQRCodeUseCase: ScanQRCodeUseCase;
  private readonly getAvailableServicesUseCase: GetAvailableServicesUseCase;
  private readonly getAvailableServicesFromEligibilityUseCase: GetAvailableServicesFromEligibilityUseCase;
  private readonly getAssignmentByIdUseCase: GetAssignmentByIdUseCase;
  private readonly createDeliveryUseCase: CreateDeliveryUseCase;
  private readonly createAssignmentUseCase: CreateAssignmentUseCase;
  private readonly updateDeliveryUseCase: UpdateDeliveryUseCase;
  private readonly getCaseDetailUseCase: GetCaseDetailUseCase;

  // Services
  private readonly authService: AuthService;
  private readonly dashboardService: DashboardService;
  private readonly scanService: ScanService;
  private readonly formDataService: FormDataService;

  private constructor() {
    // Infrastructure
    this.storageAdapter = new AsyncStorageAdapter();
    this.authRepository = new AuthRepositoryImpl(this.storageAdapter);
    this.dashboardRepository = new DashboardRepositoryImpl();
    this.scanRepository = new ScanRepositoryImpl();
    this.formDataRepository = new FormDataRepositoryImpl();

    // Use Cases
    this.loginUseCase = new LoginUseCase(this.authRepository);
    this.logoutUseCase = new LogoutUseCase(this.authRepository);
    this.getCurrentUserUseCase = new GetCurrentUserUseCase(this.authRepository);
    this.send2FACodeUseCase = new Send2FACodeUseCase(this.authRepository);
    this.verify2FACodeUseCase = new Verify2FACodeUseCase(this.authRepository);
    this.getDashboardDataUseCase = new GetDashboardDataUseCase(
      this.dashboardRepository,
    );
    this.getDeliveriesByCreatedUserUseCase = new GetDeliveriesByCreatedUserUseCase(
      this.dashboardRepository,
    );
    this.scanQRCodeUseCase = new ScanQRCodeUseCase(this.scanRepository);
    this.getAvailableServicesUseCase = new GetAvailableServicesUseCase(
      this.scanRepository,
    );
    this.getAvailableServicesFromEligibilityUseCase =
      new GetAvailableServicesFromEligibilityUseCase(this.scanRepository);
    this.getAssignmentByIdUseCase = new GetAssignmentByIdUseCase(
      this.scanRepository,
    );
    this.createDeliveryUseCase = new CreateDeliveryUseCase(
      this.scanRepository,
    );
    this.createAssignmentUseCase = new CreateAssignmentUseCase(
      this.scanRepository,
    );
    this.updateDeliveryUseCase = new UpdateDeliveryUseCase(
      this.scanRepository,
    );
    this.getCaseDetailUseCase = new GetCaseDetailUseCase(
      this.scanRepository,
    );

    // Services
    this.authService = new AuthService(
      this.loginUseCase,
      this.logoutUseCase,
      this.getCurrentUserUseCase,
      this.send2FACodeUseCase,
      this.verify2FACodeUseCase,
    );
    this.dashboardService = new DashboardService(
      this.getDashboardDataUseCase,
      this.getDeliveriesByCreatedUserUseCase,
    );
    this.scanService = new ScanService(
      this.scanQRCodeUseCase,
      this.getAvailableServicesUseCase,
      this.getAvailableServicesFromEligibilityUseCase,
      this.getAssignmentByIdUseCase,
      this.createDeliveryUseCase,
      this.createAssignmentUseCase,
      this.updateDeliveryUseCase,
      this.getCaseDetailUseCase,
    );
    this.formDataService = new FormDataService(this.formDataRepository);

    // Wire up global token refresh for all ApiClient instances
    const storage = this.storageAdapter;
    const logoutUseCase = this.logoutUseCase;

    setGlobalRefreshCallback(async () => {
      try {
        const user = await storage.getUser();
        const refreshToken = user?.refresh_token;
        if (!refreshToken) return null;

        const response = await fetch(`${API_CONFIG.sociantBaseURL}/api/auth/refresh`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        });

        if (!response.ok) return null;

        const data = await response.json();
        const newAccessToken: string = data.access_token;
        if (!newAccessToken) return null;

        // Update stored user with new token
        const updatedUser = {...user, token: newAccessToken};
        await storage.saveUser(updatedUser);

        return newAccessToken;
      } catch {
        return null;
      }
    });

    setGlobalLogoutCallback(async () => {
      await logoutUseCase.execute();
    });
  }

  public static getInstance(): ServiceContainer {
    if (!ServiceContainer.instance) {
      ServiceContainer.instance = new ServiceContainer();
    }
    return ServiceContainer.instance;
  }

  public getAuthService(): AuthService {
    return this.authService;
  }

  public getDashboardService(): DashboardService {
    return this.dashboardService;
  }

  public getScanService(): ScanService {
    return this.scanService;
  }

  public getFormDataService(): FormDataService {
    return this.formDataService;
  }
}

